import { useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isToday, isBefore, startOfDay, endOfDay } from "date-fns";

interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'error' | 'warning';
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const { data: todayRecords } = useQuery({
    queryKey: ['todayRecords'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { data, error } = await supabase
        .from('asset_records')
        .select('created_at')
        .eq('user_id', user.id)
        .gte('created_at', new Date().toISOString().split('T')[0]);

      if (error) throw error;
      return data?.length || 0;
    },
  });

  const { data: todayReminders } = useQuery({
    queryKey: ['todayReminders'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      // Get today's start and end timestamps
      const today = new Date();
      const todayStart = startOfDay(today).toISOString();
      const todayEnd = endOfDay(today).toISOString();

      const { data, error } = await supabase
        .from('reminders')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .or(`next_trigger_at.lte.${todayEnd},next_trigger_at.is.null`);

      if (error) throw error;
      return data?.length || 0;
    },
  });

  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    setNotifications(prev => [...prev, { ...notification, id }]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return {
    notifications,
    addNotification,
    removeNotification,
    todayRecords: todayRecords || 0,
    todayReminders: todayReminders || 0,
  };
} 